/**
 * Converts a storyboard/component name into a valid filename segment
 * - Removes/replaces invalid filename characters: / \ : * ? " < > |
 * - Removes spaces, hyphens, underscores (capitalizing first letter after them)
 * - Preserves original casing as much as possible
 * - Handles edge cases (empty strings, special chars, etc.)
 *
 * Examples:
 *   "My Button" => "MyButton"
 *   "user-profile" => "UserProfile"
 *   "Storyboard1Bleh" => "Storyboard1Bleh"
 *   "Header!!!" => "Header"
 *   "" => "Component"
 */
export function formatNameForFilename(name: string): string {
  if (!name || !name.trim()) {
    return "Component";
  }

  // Remove invalid filesystem characters: / \ : * ? " < > |
  const sanitized = name.replace(/[/\\:*?"<>|]/g, "");

  // Split on spaces, hyphens, underscores
  const words = sanitized.split(/[\s\-_]+/).filter((w) => w.length > 0);

  // Join words, capitalizing first letter of each but preserving rest of casing
  const result = words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");

  return result || "Component";
}

/**
 * Generates a storyboard filename using the pattern: {Name}-{idPrefix}.{ext}
 *
 * @param storyboardName - The name of the storyboard/component
 * @param storyboardId - The UUID of the storyboard
 * @param extension - File extension (e.g., ".tsx", ".jsx")
 * @returns Formatted filename like "MyButton-abc123.tsx"
 */
export function generateStoryboardFilename(
  storyboardName: string,
  storyboardId: string,
  extension: string = ".tsx",
): string {
  const formattedName = formatNameForFilename(storyboardName);
  const idPrefix = storyboardId.split("-")[0]; // First segment of UUID
  return `${formattedName}-${idPrefix}${extension}`;
}

/**
 * Generates a page folder name using the pattern: {formatted-name}-{idPrefix}
 * Page folders use lowercase names for consistency.
 *
 * @param pageName - The name of the page
 * @param pageId - The UUID of the page
 * @returns Formatted folder name like "dashboard-abc123"
 */
export function generatePageFolderName(
  pageName: string,
  pageId: string,
): string {
  const formattedName = formatNameForFilename(pageName).toLowerCase();
  const idPrefix = pageId.split("-")[0];
  return `${formattedName}-${idPrefix}`;
}

/**
 * Generates a unique name by checking against existing names.
 * If the name exists, it increments trailing numbers or adds a number suffix.
 * Uses recursion to ensure uniqueness.
 *
 * Examples (without separator - for storyboards):
 *   getUniqueName("Button", new Set(["Button"])) => "Button2"
 *   getUniqueName("Card", new Set(["Card", "Card2"])) => "Card3"
 *   getUniqueName("Header", new Set(["Footer"])) => "Header"
 *
 * Examples (with separator " " - for pages):
 *   getUniqueName("Page", new Set(["Page"]), " ") => "Page 1"
 *   getUniqueName("Page 1", new Set(["Page 1"]), " ") => "Page 2"
 *
 * @param baseName - The base name to make unique
 * @param existingNames - Set of names that already exist
 * @param separator - Optional separator before numbers (default: "" for storyboards, use " " for pages)
 * @returns A unique name that doesn't conflict with existing names
 */
export function getUniqueName(
  baseName: string,
  existingNames: Set<string>,
  separator: string = "",
): string {
  // If the base name doesn't exist, use it
  if (!existingNames.has(baseName)) {
    return baseName;
  }

  // Build regex pattern to match name ending with optional separator and number
  const escapedSeparator = separator.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`^(.*?)(${escapedSeparator})(\\d+)$`);
  const match = baseName.match(pattern);

  if (match) {
    // Name ends with separator + number - increment it and recurse
    const prefix = match[1];
    const sep = match[2];
    const number = parseInt(match[3], 10);
    const nextName = `${prefix}${sep}${number + 1}`;
    return getUniqueName(nextName, existingNames, separator);
  } else {
    // Name doesn't end with separator + number - add separator and starting number
    const startingNumber = separator ? 1 : 2;
    const nextName = `${baseName}${separator}${startingNumber}`;
    return getUniqueName(nextName, existingNames, separator);
  }
}
